<template>
  <transition-group
    name="support-popup"
    tag="div"
    class="support-popup-stack"
    aria-live="polite"
  >
    <article
      v-for="item in items"
      :key="item.id"
      class="support-popup-card"
      role="button"
      tabindex="0"
      @click="openSupport(item)"
      @keydown.enter="openSupport(item)"
      @keydown.space.prevent="openSupport(item)"
    >
      <div class="support-popup-card__icon" aria-hidden="true">
        <i class="fa fa-comments-o"></i>
      </div>
      <div class="support-popup-card__content">
        <div class="support-popup-card__topline">
          <strong>{{ item.title }}</strong>
          <button
            type="button"
            aria-label="Fechar notificação"
            @click.stop="dismiss(item.id)"
          >
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>
        <span class="support-popup-card__context">
          {{ item.context }}
        </span>
        <p>{{ item.message }}</p>
        <span class="support-popup-card__action">Abrir atendimento</span>
      </div>
    </article>
  </transition-group>
</template>

<script>
import {
  PROJECT_SUPPORT_TENANT_ID,
  resolveCurrentTenant,
} from "@/utils/supportContext";

const MAX_VISIBLE_ITEMS = 3;
const DISPLAY_TIME_MS = 9000;

function normalizeText(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export default {
  name: "SupportPushPopup",
  data() {
    return {
      items: [],
      timers: {},
      recentIds: new Set(),
    };
  },
  computed: {
    notificationsEnabled() {
      return this.$store?.state?.user?.permitirNotificacoes !== false;
    },
    currentPersonId() {
      return Number(this.$store?.state?.user?.pessoa?.id || 0);
    },
    currentTenantId() {
      return Number(resolveCurrentTenant(this.$store?.state || {})?.id || 0);
    },
  },
  watch: {
    currentPersonId() {
      this.clearItems();
    },
    currentTenantId() {
      this.clearItems();
    },
  },
  mounted() {
    if (typeof window !== "undefined") {
      window.addEventListener("support:push", this.onSupportPush);
    }
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("support:push", this.onSupportPush);
    }
    Object.values(this.timers).forEach((timer) => clearTimeout(timer));
  },
  methods: {
    clearItems() {
      Object.values(this.timers).forEach((timer) => clearTimeout(timer));
      this.timers = {};
      this.items = [];
      this.recentIds.clear();
    },
    canViewPayload(payload) {
      const scope = String(
        payload?.escopo || payload?.ticketEscopo || ""
      ).toUpperCase();
      const supportTenantId = Number(payload?.inquilinoId || 0);
      const originId = Number(payload?.inquilinoOrigemId || 0);
      const openerId = Number(payload?.operadorAberturaId || 0);
      if (!["PROJETO", "INTERNO"].includes(scope)) return false;
      if (!this.currentPersonId || !this.currentTenantId || !supportTenantId)
        return false;
      if (openerId === this.currentPersonId) {
        return (
          (originId === this.currentTenantId ||
            (scope === "PROJETO" &&
              !originId &&
              Number(payload?.clienteId || 0) === this.currentTenantId)) &&
          (scope === "PROJETO"
            ? supportTenantId === PROJECT_SUPPORT_TENANT_ID
            : supportTenantId ===
              Number(
                resolveCurrentTenant(this.$store?.state || {})?.suporteMatriz ||
                  this.currentTenantId
              ))
        );
      }
      const permissions = this.$store?.state?.permissoes || [];
      return scope === "PROJETO"
        ? supportTenantId === PROJECT_SUPPORT_TENANT_ID &&
            permissions.includes("ROLE_Suporte_Atendimento")
        : scope === "INTERNO" &&
            supportTenantId ===
              Number(
                resolveCurrentTenant(this.$store?.state || {})?.suporteMatriz ||
                  this.currentTenantId
              ) &&
            permissions.includes("ROLE_SuporteInterno_Atendimento");
    },
    onSupportPush(event) {
      if (
        !this.notificationsEnabled ||
        typeof document === "undefined" ||
        document.visibilityState !== "visible"
      ) {
        return;
      }

      const detail = event?.detail || {};
      const payload = detail?.payload || {};
      if (!this.canViewPayload(payload)) return;
      const ticketId = Number(
        payload?.ticketId || payload?.ticketIdRef || payload?.idTicket || 0
      );
      if (!ticketId) return;

      const evento = String(payload?.evento || "MSG").toUpperCase();
      if (!["MSG", "NOVO"].includes(evento)) return;

      const senderId = Number(
        payload?.remetenteId || payload?.pessoaId || payload?.operadorId || 0
      );
      if (senderId && senderId === this.currentPersonId) return;

      const message = this.resolveMessage(payload);
      if (!message) return;

      const messageId =
        payload?.mensagemId || payload?.messageId || payload?.id || null;
      const dedupeId = String(
        messageId || `${ticketId}:${payload?.dtEnvio || ""}:${message}`
      );
      if (this.recentIds.has(dedupeId)) return;
      this.recentIds.add(dedupeId);
      setTimeout(() => this.recentIds.delete(dedupeId), 60000);

      const nsr = Number(payload?.nsr || payload?.ticketNsr || 0);
      const subject = normalizeText(
        payload?.assunto || payload?.ticketAssunto || "Atendimento"
      );
      const sender = normalizeText(
        payload?.remetenteNome ||
          payload?.pessoaNome ||
          (String(payload?.remetente || "").toUpperCase() === "IA"
            ? "Caju"
            : "Suporte")
      );
      const notificationTitle = normalizeText(
        detail?.message?.notification?.title || detail?.rawData?.title
      );
      const id = `${dedupeId}:${Date.now()}`;
      const item = {
        id,
        ticketId,
        escopo:
          String(
            payload?.escopo || payload?.ticketEscopo || "PROJETO"
          ).toUpperCase() === "INTERNO"
            ? "INTERNO"
            : "PROJETO",
        operadorAberturaId: Number(payload?.operadorAberturaId || 0),
        title: notificationTitle || `Nova mensagem de ${sender}`,
        context: `Ticket #${nsr || ticketId} · ${subject}`,
        message,
      };

      this.items = [item, ...this.items].slice(0, MAX_VISIBLE_ITEMS);
      this.timers[id] = setTimeout(() => this.dismiss(id), DISPLAY_TIME_MS);
    },
    resolveMessage(payload) {
      const text = normalizeText(
        payload?.mensagem || payload?.preview || payload?.conteudo
      );
      if (text) return text;
      const firstAttachment = Array.isArray(payload?.anexos)
        ? payload.anexos[0]
        : null;
      return firstAttachment
        ? `Novo anexo: ${normalizeText(firstAttachment?.nome) || "arquivo"}`
        : "";
    },
    dismiss(id) {
      if (this.timers[id]) {
        clearTimeout(this.timers[id]);
        delete this.timers[id];
      }
      this.items = this.items.filter((item) => item.id !== id);
    },
    openSupport(item) {
      const permissions = this.$store?.state?.permissoes || [];
      const attendantRole =
        item.escopo === "INTERNO"
          ? "ROLE_SuporteInterno_Atendimento"
          : "ROLE_Suporte_Atendimento";
      const isRequester =
        item.operadorAberturaId > 0 &&
        item.operadorAberturaId === this.currentPersonId;
      const isAttendant = !isRequester && permissions.includes(attendantRole);
      const path = isAttendant
        ? item.escopo === "INTERNO"
          ? "/dashboard/suporte-interno/atendimento"
          : "/dashboard/suporte/atendimento"
        : "/dashboard/suporte";

      this.dismiss(item.id);
      this.$router
        .push({
          path,
          query: { escopo: item.escopo, ticketId: String(item.ticketId) },
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.support-popup-stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: min(390px, calc(100vw - 24px));
  position: fixed;
  right: 18px;
  top: 18px;
  width: 390px;
  z-index: 10050;
}

.support-popup-card {
  align-items: flex-start;
  background: #fff;
  border: 1px solid rgba(81, 106, 120, 0.18);
  border-left: 4px solid #516a78;
  border-radius: 12px;
  box-shadow: 0 14px 38px rgba(30, 48, 58, 0.2);
  cursor: pointer;
  display: flex;
  gap: 12px;
  padding: 15px;
}

.support-popup-card:focus-visible {
  outline: 3px solid rgba(81, 106, 120, 0.3);
  outline-offset: 2px;
}

.support-popup-card__icon {
  align-items: center;
  background: #edf2f4;
  border-radius: 10px;
  color: #516a78;
  display: flex;
  flex: 0 0 38px;
  font-size: 18px;
  height: 38px;
  justify-content: center;
}

.support-popup-card__content {
  min-width: 0;
  width: 100%;
}

.support-popup-card__topline {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  justify-content: space-between;
}

.support-popup-card__topline strong {
  color: #263b46;
  font-size: 0.94rem;
  line-height: 1.3;
}

.support-popup-card__topline button {
  background: transparent;
  border: 0;
  color: #829099;
  cursor: pointer;
  padding: 0 2px;
}

.support-popup-card__context {
  color: #667984;
  display: block;
  font-size: 0.76rem;
  margin-top: 3px;
}

.support-popup-card p {
  color: #354b57;
  display: -webkit-box;
  font-size: 0.86rem;
  line-height: 1.45;
  margin: 9px 0 7px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.support-popup-card__action {
  color: #516a78;
  font-size: 0.76rem;
  font-weight: 700;
}

.support-popup-enter-active,
.support-popup-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.support-popup-enter,
.support-popup-leave-to {
  opacity: 0;
  transform: translateX(24px);
}

@media (max-width: 600px) {
  .support-popup-stack {
    right: 12px;
    top: 12px;
  }
}
</style>
